<template>
  <div class="page">

    <div class="page__subheader">
      <ui-link-button-meek
        :size="UI.SIZE.SMALL"
        :route="parentRoute"
      >
        <ui-icon
          name="navigate_before"
        />
        Projets
      </ui-link-button-meek>
    </div>

    <div class="page__main">

      <ui-cloak
        :is-visible="fetchState.status.value === 'success'"
        :sustain-phase-duration="800"
      >
        <template #cloak>
          <b-card class="card--leveled">
            <hamac-project-create-placeholder />
          </b-card>
        </template>

        <template #default>
          <b-card class="card--leveled">
            <hamac-project-create-organism
              v-bind="{
                searchTeamCallback,
                searchTopicCallback,
                searchProfessionCallback,
                searchAnalyticCallback,
                searchTrackingNumberCallback,
                createTrackingNumberCallback,
                createAnalyticCallback,
                isPending,
                submitCallback: createCallback,
                teamTagList,
                customDataList,
                isStructureConnected,
              }"
              :default-data="defaultData"
            />
          </b-card>
        </template>

      </ui-cloak>

    </div>

  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/sass/modules/layout/variables'

$form-width: 880px

.page
  padding: ($layout-spacer-y) ($layout-spacer-x * 1.5)
  background-color: $background-tertiary

  &__subheader
    margin-bottom: $layout-spacer-y * .75

  &__main
    max-width: $form-width

</style>

<script lang="ts">
import { wrap as wrapInput } from '~/utils/infra/common/types'
import Console from '~/utils/lib/core/console'
import { clone, delay, join, noop, notEmpty, tapCatch, tapThen, toString } from '~/utils/lib/core/prelude'
import { addCopyNumber } from '~/utils/lib/core/helpers'
import { getListRoute, getDetailsRoute } from '~/stores/hamac/project/index'
import { MENU_ITEM, NAVIGATION_SCOPE } from '~/utils/lib/layout/types'
import { useLegacyPageFetch } from '~/composables/core/hook'
import { useToastBuilder } from '~/composables/layout/toast'
import { useCompanyRequiredNavigationContext } from '~/composables/layout/navigation'
import { useLinkHandler } from '~/composables/core/link-handler'
import type { Topic } from '~/stores/hamac/topic'
import type { Profession } from '~/stores/hamac/profession'
import { useStore_hamac_project_analytic, type Analytic } from '~/stores/hamac/project/analytic'
import { useStore_hamac_team_list, type Filters, type Team } from '~/stores/hamac/team/list'
import type { TrackingNumber } from '~/stores/hamac/tracking-number/details'
import type { DefaultData as ProjectDefaultData} from '~/components/hamac/project/create/Organism.vue'
import { useStore_hamac_contractTemplate_customData, type CustomData } from '~/stores/hamac/contract-template/custom-data'
import type { ListInputSchema } from '~/utils/infra/common/types_'
import { useStore_hamac_team_tag } from '~/stores/hamac/team/tag'
import { useStore_hamac_trackingNumber_list } from '~/stores/hamac/tracking-number/list'
import { useStore_hamac_project_details } from '~/stores/hamac/project/details'
import { useStore_hamac_project_profession } from '~/stores/hamac/project/profession'
import { useStore_hamac_project_topic } from '~/stores/hamac/project/topic'
import { useStore_hamac_project_trackingNumber } from '~/stores/hamac/project/tracking-number'
import { useStore_hamac_structure_ping } from '~/stores/hamac/structure/ping'
import type { Context } from '~/composables/layout/table-provider'
import { useStore_hamac_topic_company_list } from '~/stores/hamac/topic/company/list'
import { useStore_hamac_profession_list } from '~/stores/hamac/profession/list'
import { useStore_hamac_analytic_list } from '~/stores/hamac/analytic/list'
import { useStore_hamac_trackingNumber } from '~/stores/hamac/tracking-number'
import { useStore_hamac_analytic } from '~/stores/hamac/analytic'
import { useStore_hamac_team_details } from '~/stores/hamac/team/details'

const console = new Console('component:project-duplicate')

export default defineNuxtComponent({
  setup() {
    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.COMPANY_REQUIRED,
      menuItem: MENU_ITEM.EMBAUCHE,
    })
    const navigationContext = useCompanyRequiredNavigationContext()

    useHead({
      title: join(' ', [
        `Projet → duplication`,
        `— ${navigationContext.fromCompanyLevel().company.raison_sociale}`,
      ]),
    })

    const {
      fetchState,
      fetchWrapper,
    } = useLegacyPageFetch()

    const ToastBuilder = useToastBuilder()

    const store_project_details = useStore_hamac_project_details()
    const store_project_analytic = useStore_hamac_project_analytic()
    const store_project_profession = useStore_hamac_project_profession()
    const store_project_topic = useStore_hamac_project_topic()
    const store_project_trackingNumber = useStore_hamac_project_trackingNumber()
    const store_team_list = useStore_hamac_team_list()
    const store_team_tag = useStore_hamac_team_tag()
    const store_team_details = useStore_hamac_team_details()
    const store_trackingNumber = useStore_hamac_trackingNumber()
    const store_trackingNumber_list = useStore_hamac_trackingNumber_list()
    const store_project_contractTemplate_customData = useStore_hamac_contractTemplate_customData()
    const store_structure_ping = useStore_hamac_structure_ping()
    const store_topic_company_list = useStore_hamac_topic_company_list()
    const store_profession_list = useStore_hamac_profession_list()
    const store_analytic = useStore_hamac_analytic()
    const store_analytic_list = useStore_hamac_analytic_list()

    return {
      ...navigationContext,
      ...useLinkHandler(),
      fetchState,
      fetchWrapper,
      ToastBuilder,
      store_team_tag,
      store_trackingNumber_list,
      store_project_details,
      store_project_analytic,
      store_project_profession,
      store_project_topic,
      store_project_trackingNumber,
      store_project_contractTemplate_customData,
      store_structure_ping,
      store_team_list,
      store_topic_company_list,
      store_profession_list,
      store_analytic_list,
      store_trackingNumber,
      store_analytic,
      store_team_details,
    }
  },
  data() {
    const parentRoute = (() => {
      const {
        structure: { id },
        company: { code_societe }
        // @ts-expect-error: "data" hook not injecting "setup" dependencies
      } = this.fromCompanyLevel()

      return getListRoute(id, code_societe)
    })()

    return {
      parentRoute,
      isPending: false,
      isStructureConnected: false,
      // lazy
      topicList: [] as Array<Topic>,
      professionList: [] as Array<Profession>,
      analyticList: [] as Array<Analytic>,
      teamTagList: [] as Team["tags"],
      trackingNumberList: [] as Array<TrackingNumber>,
      defaultData: null as unknown as Partial<ProjectDefaultData>,
      customDataList: [] as Array<CustomData>,
    }
  },
  created() {
    this.fetchWrapper(
      () => this.fetch()
    )
  },
  methods: {
    searchTeamCallback(context :Context<Filters>) {
      const opts = {
        input: wrapInput(context),
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe
      }

      return this.store_team_list.read(opts)
        .catch(tapCatch(err => {
          console.warn('searchTeamCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Recherche d'équipes`)
            .show()
        }))
    },
    searchTopicCallback(context) {
      const opts = {
        input: wrapInput(context),
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe
      }

      return this.store_topic_company_list.read(opts)
        .catch(tapCatch(err => {
          console.warn('searchTopicCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Recherche de rubriques`)
            .show()
        }))
    },
    searchProfessionCallback(context) {
      const opts = {
        input: wrapInput(context),
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe
      }

      return this.store_profession_list.read(opts)
        .catch(tapCatch(err => {
          console.warn('searchProfessionCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Recherche de professions`)
            .show()
        }))
    },
    searchAnalyticCallback(context) {
      const opts = {
        input: wrapInput(context),
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe
      }

      return this.store_analytic_list.read(opts)
        .catch(tapCatch(err => {
          console.warn('searchAnalyticCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Recherche d'analytiques`)
            .show()
        }))
    },
    searchTrackingNumberCallback(context) {
      const opts = {
        input: wrapInput(context),
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe
      }

      return this.store_trackingNumber_list.read(opts)
        .catch(tapCatch(err => {
          console.warn('searchTrackingNumberCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Recherche de numéro d'objet`)
            .show()
        }))
    },
    createTrackingNumberCallback(data) {
      const opts = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        data
      }
      return this.store_trackingNumber.create(opts)
        .then(tapThen((({ numero_objet }) => {
          // ⚠ Defensive delay to make sure the toast is visible after the modals are closed
          delay(300)
            .then(() => {
              this.ToastBuilder
                .success(`Le numéro d'objet "${numero_objet}" a bien été ajouté au projet`)
                .title(`Numéro d'objet ajouté`)
                .show()
            })
        })))
        .catch(tapCatch(err => {
          console.warn('createTrackingNumberCallback', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Ajout d'un numéro d'objet`)
            .show()
        }))
    },
    createAnalyticCallback(data) {
      const opts = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        uuid_hamac_projet: this.$route.params.projectCode,
        data
      }

      return this.store_analytic.create(opts)
    },
    createCallback(data) {
      const opts = {
        data,
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
      }

      this.isPending = true

      return this.store_project_details.create(opts)
        .then(({ uuid_hamac_projet }) => {
          const { id } = this.fromCompanyLevel().structure
          const { code_societe } = this.fromCompanyLevel().company

          const url = getDetailsRoute(id, code_societe, uuid_hamac_projet)

          this.goToRoute(url)
            .then(
              () => this.ToastBuilder.success().title(`Projet ajouté !`).show()
            )
            .catch(noop)
        })
        .catch(err => {
          this.isPending = false

          console.warn('create', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Duplication projet`)
            .show()
        })
    },
    fetch() {
      const options = {
        uuidData: this.fromCompanyLevel().structure.uuidData,
        code_societe: this.fromCompanyLevel().company.code_societe,
        uuid_hamac_projet: this.$route.params.projectCode as string,
      }

      const input = { per_page: 10000 } satisfies ListInputSchema // eager

      return Promise.all([
          this.store_team_tag.read(
            options
          ),
          this.store_trackingNumber_list.read(
            { ...options, input }
          ),
          this.store_project_details.read(
            options
          ),
          this.store_project_analytic.read(
            { ...options, input }
          ),
          this.store_project_profession.read(
            { ...options, input }
          ),
          this.store_project_topic.read(
            { ...options, input }
          ),
          this.store_project_trackingNumber.read(
            { ...options, input }
          ),
          this.store_project_contractTemplate_customData.read_(
            { uuidData: options.uuidData }
          ),
          this.store_structure_ping.exec_(
            { ...options }
          ),
        ])
        .then(results => {
          // Component data
          this.teamTagList = results[0]
          this.trackingNumberList = clone(results[1].data)

          const defaultData = {
            label: addCopyNumber(results[2].label),

            tags: results[2].tags,

            contractType: results[2].type_contrat,

            limitedAccessibility: notEmpty(results[2].uuid_hamac_equipe),

            selectedAnalyticList: clone(
              results[3].data
            ),

            selectedProfessionList: clone(
              results[4].data
            ),

            selectedTopicList: clone(
              results[5].data
            ),

            selectedTrackingNumberList: clone(
              results[6].data
            ),

            dictionary: results[2].perso
          }

          this.defaultData = defaultData
          this.customDataList = results[7]
          this.isStructureConnected = results[8]


          // retrieve whole team details (if any)
          const hasTeam = notEmpty(results[2].uuid_hamac_equipe)

          const findTeamPromise = (() => {
            switch (hasTeam) {

              case false:
                return Promise.resolve()

              case true:
                return this.store_team_details.read(
                    {
                      ...options,
                      // @ts-expect-error: TypeScript narrowing/guard limitation
                      uuid_hamac_equipe: results[2].uuid_hamac_equipe
                    }
                  )
                  .then(team => this.defaultData.team = team)
                  .then(noop)
            }
          })()

          return findTeamPromise
        })
        .catch(tapCatch(err => {
          console.warn('fetch', err)
          this.ToastBuilder
            .error(toString(err))
            .title(`Duplication projet`)
            .show()
        }))
    }
  }
})
</script>
